import { Injectable } from "@angular/core";
import { HttpService } from "./http.service";
import { environment } from "@environments/environment";
import { CallLog, MakeCallDto } from "@models/call";
import { Observable, Subject, map, Subscription } from "rxjs";
import { SessionService } from "./session.service";
// import { AudioStreamer } from "./audio-streamer.service";
import { WebSocketService } from "./socket.service";
import { AgentProfile } from "@models/agent";

import {
    ConnectionState,
    createLocalAudioTrack,
    DataPacket_Kind,
    LocalAudioTrack,
    Participant,
    ParticipantEvent,
    RemoteAudioTrack,
    RemoteParticipant,
    RemoteTrack,
    RemoteTrackPublication,
    Room,
    RoomEvent,
    Track,
} from 'livekit-client';
import { chatWidgetType } from '@models/chat';

/** RPC method name for human takeover (takeover_start) / hand-back (takeover_end). Must match Talkgenie-Livekit-Worker constants. */
export const TAKEOVER_RPC_METHOD = 'takeover';

/** LiveKit `publishData` topic for typing indicators (room fan-out; no worker relay). */
export const TYPING_DATA_TOPIC = 'tg_typing';
export const TG_TYPING_PAYLOAD_V1 = 'typing_v1' as const;

const GCS_HOST_PREFIX = 'https://storage.googleapis.com/';

/** lk.transcription echo of LiveKit ImageContent — not visitor-facing chat text. */
export function isImageContentTranscriptionEcho(text: string): boolean {
    return /type=['"]image_content['"]/i.test(text ?? '');
}

function isGcsImageUrl(url: string): boolean {
    const u = (url ?? '').trim();
    if (!u.startsWith(GCS_HOST_PREFIX)) return false;
    try {
        const path = new URL(u).pathname.toLowerCase();
        return /\.(png|jpe?g|gif|webp|svg|bmp|ico)(\?|$)/.test(path);
    } catch {
        return false;
    }
}

/** Plain URL, caption+URL lines, or img_v1 JSON from widget image send. */
export function isGcsImageUrlInText(text: string): boolean {
    const t = (text ?? '').trim();
    if (!t) return false;
    if (t.startsWith('{')) {
        try {
            const data = JSON.parse(t) as { tg?: string; url?: string };
            if (data?.tg === 'img_v1' && typeof data.url === 'string') {
                return isGcsImageUrl(data.url);
            }
        } catch {
            /* not JSON */
        }
    }
    if (isGcsImageUrl(t)) return true;
    return t.split('\n').some((line) => isGcsImageUrl(line.trim()));
}

function isGcsAudioUrl(text: string): boolean {
    const t = (text ?? '').trim();
    if (!t.startsWith(GCS_HOST_PREFIX)) return false;
    try {
        const pathname = new URL(t).pathname.toLowerCase().split('?')[0];
        return /\.(webm|ogg|mp3|m4a|wav|flac|opus)$/.test(pathname);
    } catch {
        return false;
    }
}

/** Serializable LiveKit participant subset for typing UI (widget + dashboard). */
export interface RemoteTypingParticipant {
    identity: string;
    /** Display name from JWT (may be empty). */
    name: string;
    /** Server-assigned participant SID (e.g. PA_…). */
    sid: string;
    /** Opaque client metadata from JWT when set. */
    metadata?: string;
}

/**
 * Dashboard human agents (handoff token). Server mints `{userId}-{uuid}` — see
 * talkgenie-server `chatwidget-handoff-to-human`. Legacy: `human-*`, pure numeric user id.
 */
export function isDashboardHumanAgentIdentity(identity: string | undefined | null): boolean {
    const id = (identity ?? '').trim();
    if (!id || id.startsWith('enduser-') || id.startsWith('chat-') || id.startsWith('agent-')) return false;
    if (id.startsWith('spectator-')) return false;
    if (id.startsWith('human-')) return true;
    if (/^\d+$/.test(id)) return true;
    if (/^\d+-[0-9a-f-]+(?:-[0-9a-f-]+)*$/i.test(id)) return true;
    return false;
}

/** Plain snapshot for typing events (shared by CallService and the multi-room livechat service). */
export function snapshotRemoteTypingParticipant(participant: Participant): RemoteTypingParticipant {
    const identity = (participant.identity ?? '').trim();
    const name = (participant.name ?? '').trim();
    const sid = (participant.sid ?? '').trim();
    const md = participant.metadata;
    if (md != null && String(md).trim() !== '') {
        return { identity, name, sid, metadata: String(md).trim() };
    }
    return { identity, name, sid };
}

/** Emitted when a remote participant sends a typing ping via {@link TYPING_DATA_TOPIC}. */
export type RemoteTypingEvent = {
    source: 'enduser' | 'human' | 'agent';
    isTyping: boolean;
    participant: RemoteTypingParticipant;
    /** Worker (agent): UI label for long LLM vs tool execution */
    phase?: 'generating' | 'tools';
    hint?: string | null;
};
export type TakeoverRpcAction = 'takeover_start' | 'takeover_end';

/** Widget visitor: takeover, hand-back, join, or staff left (visitor sees remote disconnect). */
export type HumanAgentPresentEvent =
    | { present: true; agentName?: string; agentIdentity?: string; agentImage?: string }
    | { present: false }
    | {
          additionalJoin: true;
          agentName?: string;
          watchingOnly?: boolean;
          agentIdentity?: string;
          agentImage?: string;
      }
    | { humanLeft: true; agentName?: string; leftIdentity?: string; lastHuman: boolean };

function parseTakeoverRpcPayload(payload: string | object): {
    action?: string;
    humanAgentName?: string;
    humanAgentIdentity?: string;
    humanAgentImage?: string;
    watchingOnly?: boolean;
} {
    try {
        const raw = typeof payload === 'string' ? JSON.parse(payload) : payload;
        if (!raw || typeof raw !== 'object') return {};
        const o = raw as Record<string, unknown>;
        const inner =
            typeof o['payload'] === 'string'
                ? (JSON.parse(o['payload'] as string) as Record<string, unknown>)
                : typeof o['payload'] === 'object' && o['payload'] !== null
                  ? (o['payload'] as Record<string, unknown>)
                  : o;
        const actionRaw = typeof inner['action'] === 'string' ? inner['action'].trim() : undefined;
        const action = actionRaw ? actionRaw.toLowerCase() : undefined;
        const humanAgentName =
            typeof inner['humanAgentName'] === 'string'
                ? (inner['humanAgentName'] as string).trim()
                : undefined;
        const w = inner['watchingOnly'];
        const watchingOnly =
            w === true || w === 'true' || w === 1 || w === '1';
        const humanAgentIdentity =
            typeof inner['humanAgentIdentity'] === 'string'
                ? (inner['humanAgentIdentity'] as string).trim()
                : undefined;
        const humanAgentImage =
            typeof inner['humanAgentImage'] === 'string'
                ? (inner['humanAgentImage'] as string).trim()
                : undefined;
        return { action, humanAgentName, watchingOnly, humanAgentIdentity, humanAgentImage };
    } catch {
        return {};
    }
}


@Injectable({
    providedIn: 'root'
})
export class CallService {

    public onSocketConnected: Subject<string> = new Subject<string>();
    public onTranscription: Subject<string> = new Subject<string>();
    /** Visitor text typed via lk.chat (text widget); use in dashboard live view only — not merged into widget UI. */
    public onEndUserChatText: Subject<string> = new Subject<string>();
    public onAudioData: Subject<Blob> = new Subject<Blob>();
    public onTextResponse: Subject<string> = new Subject<string>();
    public onUserSpeaking: Subject<boolean> = new Subject<boolean>();
    public onEndCall: Subject<boolean> = new Subject<boolean>();
    /** Ensures {@link onEndCall} fires once per LiveKit session (duplicate disconnect events are common). */
    private sessionEndEventEmitted = false;
    public sendMessage: Subject<{text: string, channel:string}> = new Subject<{text: string, channel:string}>();
    /** Visitor widget: human took over (optional name) or handed back to AI. */
    public onHumanAgentPresent: Subject<HumanAgentPresentEvent> = new Subject<HumanAgentPresentEvent>();
    /** Emits human agent messages with optional sender name (so widget/dashboard can show who wrote). */
    public onHumanAgentTextResponse: Subject<{ name?: string; message: string; image?: string }> = new Subject<{
        name?: string;
        message: string;
        image?: string;
    }>();
    /** Visitor ↔ human typing via {@link TYPING_DATA_TOPIC} (single-room / widget). */
    public onRemoteTyping: Subject<RemoteTypingEvent> = new Subject<RemoteTypingEvent>();
    /**
     * AI assistant text from lk.transcription (agent identity): cumulative chunks while streaming, then done.
     * Prefer this over {@link onTextResponse} for chat widget incremental display.
     */
    public onAgentAiTextStream: Subject<{ text: string; done: boolean }> = new Subject<{
        text: string;
        done: boolean;
    }>();
    public room: Room;
    
    // Add subscription tracking
    private currentCallSubscription: Subscription | null = null;
    private isConnecting: boolean = false;
    private isConnected: boolean = false;

    private audioTrack: LocalAudioTrack | null = null;

    private agentAudioElement: HTMLAudioElement | null = null;
    
    private isMuted: boolean = false;

    private staffDisplayNameByIdentity = new Map<string, string>();
    private staffImageByIdentity = new Map<string, string>();
    /** Display name → image URL (for chat_response_human when only name is known). */
    private staffImageByName = new Map<string, string>();

    /**
     * Skip the next end-user lk.transcription row when visitor_lk_chat already carried
     * the GCS URL (voice STT echo or image ImageContent echo).
     */
    private skipNextUserTranscription = false;

    /** Dashboard staff / spectator (not visitor, not AI agent). */
    private isRoomStaffIdentity(identity: string | undefined | null): boolean {
        const id = (identity ?? '').trim();
        if (!id || id.startsWith('agent-') || id.startsWith('chat-') || id.startsWith('enduser-'))
            return false;
        return id.startsWith('spectator-') || isDashboardHumanAgentIdentity(id);
    }

    /** How many remote staff/spectators are still in the room (visitor sees this after someone leaves). */
    countRemoteStaffParticipants(): number {
        if (!this.room) return 0;
        let n = 0;
        for (const p of this.room.remoteParticipants.values()) {
            if (this.isRoomStaffIdentity(p.identity)) n++;
        }
        return n;
    }

    private registerStaffProfile(identity?: string, name?: string, image?: string): void {
        const id = identity?.trim();
        const n = name?.trim();
        const img = image?.trim();
        if (id && n) this.staffDisplayNameByIdentity.set(id, n);
        if (id && img) this.staffImageByIdentity.set(id, img);
        if (n && img) this.staffImageByName.set(n, img);
    }

    private removeStaffImageForIdentity(identity: string): void {
        const id = identity.trim();
        const name = this.staffDisplayNameByIdentity.get(id);
        this.staffImageByIdentity.delete(id);
        if (name) {
            const stillSameName = [...this.staffDisplayNameByIdentity.entries()].some(
                ([otherId, n]) => otherId !== id && n === name,
            );
            if (!stillSameName) this.staffImageByName.delete(name);
        }
    }

    private clearStaffMaps(): void {
        this.staffDisplayNameByIdentity.clear();
        this.staffImageByIdentity.clear();
        this.staffImageByName.clear();
    }

    private getParticipantRole(identity: string | undefined | null): 'agent' | 'enduser' | 'human' | 'unknown' {
        const id = (identity ?? '').trim();
        if (id.startsWith('agent-')) return 'agent';
        // Widget / web visitors use chat-*; some flows use enduser-*
        if (id.startsWith('enduser-') || id.startsWith('chat-')) return 'enduser';
        if (id.startsWith('spectator-') || isDashboardHumanAgentIdentity(id)) return 'human';
        return 'unknown';
    }

    /**
     * Dispatches {@link TG_TYPING_PAYLOAD_V1} packets to {@link onRemoteTyping}.
     * Some LiveKit / embed paths omit `topic` on {@link RoomEvent.DataReceived}, so we also accept
     * untagged JSON when it matches the typing payload shape (fixes tool-phase / “deep thinking” UI).
     */
    private tryDispatchTypingDataPacket(
        payload: Uint8Array,
        participant: Participant | undefined,
        topic: string | undefined,
    ): boolean {
        if (!this.room || !participant) return false;
        const id = participant.identity;
        if (id === this.room.localParticipant.identity) return false;

        const topicOk =
            topic === TYPING_DATA_TOPIC ||
            topic === undefined ||
            topic === null ||
            (typeof topic === 'string' && topic.trim() === '');
        if (!topicOk) return false;

        const decodedData = new TextDecoder().decode(payload);
        let raw: { tg?: string; typing?: unknown; phase?: string; hint?: unknown };
        try {
            raw = JSON.parse(decodedData) as { tg?: string; typing?: unknown; phase?: string; hint?: unknown };
        } catch {
            return false;
        }
        if (raw.tg !== TG_TYPING_PAYLOAD_V1) return false;

        const role = this.getParticipantRole(id);
        if (role !== 'enduser' && role !== 'human' && role !== 'agent') return false;

        const sessionClosed = (raw as { session_closed?: unknown }).session_closed;
        if (sessionClosed === true && role === 'agent') {
            void this.endSession(true);
            return true;
        }

        if (typeof raw.typing !== 'boolean') return false;

        const phase =
            raw.phase === 'tools' || raw.phase === 'generating' ? raw.phase : undefined;
        const hint =
            typeof raw.hint === 'string' && raw.hint.trim()
                ? raw.hint.trim()
                : raw.hint === null || raw.hint === ''
                  ? null
                  : undefined;
        this.onRemoteTyping.next({
            source: role === 'enduser' ? 'enduser' : role === 'agent' ? 'agent' : 'human',
            isTyping: raw.typing,
            participant: snapshotRemoteTypingParticipant(participant),
            ...(phase ? { phase } : {}),
            ...(hint !== undefined ? { hint } : {}),
        });
        return true;
    }

    /** Count dashboard human agents in room, excluding one identity (e.g. participant who just disconnected). */
    private countDashboardHumanAgentsInRoom(excludeIdentity?: string): number {
        if (!this.room) return 0;
        const ex = (excludeIdentity ?? '').trim();
        let n = 0;
        for (const p of this.room.remoteParticipants.values()) {
            if (ex && p.identity === ex) continue;
            if (isDashboardHumanAgentIdentity(p.identity)) n++;
        }
        return n;
    }

    constructor(private http: HttpService,
        private sessionSvc: SessionService,
        // private audioStreamer: AudioStreamer,
        private socketSvc: WebSocketService
    ){

        this.sendMessage.subscribe(async (userMessage: {text: string, channel:string}) => {
            // Prefer LiveKit room: use sendText with lk.chat per https://docs.livekit.io/agents/multimodality/text/#sending-from-frontend
            if (this.room?.state === ConnectionState.Connected) {
                await this.room.localParticipant.sendText(userMessage.text, { topic: userMessage.channel });
            }
        });
    }

    /**
     * Generic helper to perform an RPC method call to a remote participant.
     * @param method The name of the RPC method to call.
     * @param payload The payload to send (string, or converts object via JSON.stringify).
     * @param destinationIdentity The identity of the remote participant to call.
     * @returns The RPC response as string, or null if not connected.
     */
    /** Prefer `agent-` prefix for the AI so identities like `chat-agent-*` are not mistaken for the agent. */
    private findRemoteParticipantForRpc(destinationIdentity: string): RemoteParticipant | undefined {
        if (!this.room) return undefined;
        const remotes = Array.from(this.room.remoteParticipants.values());
        const d = destinationIdentity.trim();
        if (d === 'agent') {
            return remotes.find((p) => p.identity.startsWith('agent-'));
        }
        return remotes.find((p) => p.identity.includes(d));
    }

    /**
     * Broadcast typing state to everyone in the room (human agent ↔ visitor).
     * Uses {@link TYPING_DATA_TOPIC} reliable data packets.
     */
    publishTypingStatus(isTyping: boolean): void {
        if (!this.room || this.room.state !== ConnectionState.Connected) return;
        const payload = JSON.stringify({ tg: TG_TYPING_PAYLOAD_V1, typing: isTyping });
        const enc = new TextEncoder().encode(payload);
        this.room.localParticipant
            .publishData(enc, { reliable: true, topic: TYPING_DATA_TOPIC })
            .catch(() => {});
    }

    async performRpcGeneric({
        method,
        payload,
        destinationIdentity,
    }: {
        method: string,
        payload: string | object,
        destinationIdentity: string,
    }): Promise<string | null> {
        if (!this.room || this.room.state !== ConnectionState.Connected) {
            return null;
        }
        const participant = this.findRemoteParticipantForRpc(destinationIdentity);
        if (!participant) {
            return null;
        }
        const serializedPayload = typeof payload === "string" ? payload : JSON.stringify(payload);
        try {
            const response = await this.room.localParticipant.performRpc({
                destinationIdentity: participant.identity,
                method,
                payload: serializedPayload,
            });
            return response;
        } catch (err) {
            console.error(`[performRpcGeneric] RPC failed:`, err);
            return null;
        }
    }

    /**
     * Notify the widget visitor (chat-* / enduser-*) so the UI can show human join / hand-back.
     */
    async performTakeoverNotifyVisitor(options: {
        action: TakeoverRpcAction | 'human_arrived';
        humanAgentName?: string;
        /** Must match LiveKit local participant identity (for leave message by name). */
        humanAgentIdentity?: string;
        humanAgentImage?: string;
        watchingOnly?: boolean;
    }): Promise<void> {
        if (!this.room || this.room.state !== ConnectionState.Connected) return;
        const visitor = Array.from(this.room.remoteParticipants.values()).find(
            (p) => p.identity.startsWith('chat-') || p.identity.startsWith('enduser-'),
        );
        if (!visitor) return;
        const body: Record<string, string> = { action: options.action };
        if (options.humanAgentName?.trim()) body['humanAgentName'] = options.humanAgentName.trim();
        if (options.humanAgentIdentity?.trim()) body['humanAgentIdentity'] = options.humanAgentIdentity.trim();
        if (options.humanAgentImage?.trim()) body['humanAgentImage'] = options.humanAgentImage.trim();
        if (options.action === 'human_arrived' && options.watchingOnly) body['watchingOnly'] = '1';
        try {
            await this.room.localParticipant.performRpc({
                destinationIdentity: visitor.identity,
                method: TAKEOVER_RPC_METHOD,
                payload: JSON.stringify(body),
            });
        } catch {
            /* visitor may have left */
        }
    }

    public placeCall(makeCallDto: MakeCallDto): Observable<CallLog> {
        return this.http.post(`${environment.apiUrl}/${this.sessionSvc.getUserWorkspace().uid}/twilio/initiate`, makeCallDto).pipe(map((callLogData: any)=>{
            let callLog: CallLog = callLogData.data;
            return callLog;
        }))
    }

    public intiateWebcall(agentId: string): Observable<any> {
        return this.http.post(`${environment.apiUrl}/${this.sessionSvc.getUserWorkspace().uid}/voicebot/initiateWebCall/${agentId}`, {}).pipe(map((callLogData: any)=>{
           return callLogData.data;
        }))
    }

    /**
     * Start a web call with an agent profile (will fetch token from API)
     */
    public async startWebCall(agentProfile: AgentProfile): Promise<void>;
    
    /**
     * Start a web call with a pre-obtained token (skips API call).
     * chatMode: 'ChatWidgetText' for text-only (no audio track), 'ChatWidgetAudio' or other for voice.
     * participantDisplayName: shown to agents / worker (LiveKit participant name); e.g. widget pre-chat full name.
     */
    public async startWebCall(tokenData: {
        wsUrl: string;
        token: string;
        chatMode?: string;
        participantDisplayName?: string;
    }): Promise<void>;
    
    /**
     * Implementation of startWebCall with proper type handling
     */
    public async startWebCall(
        profileOrToken: AgentProfile | {
            wsUrl: string;
            token: string;
            chatMode?: string;
            participantDisplayName?: string;
        },
    ): Promise<void> {
        // Prevent multiple simultaneous connection attempts
        if (this.isConnecting) {
            console.warn("Connection already in progress");
            return;
        }

        // Disconnect existing connection if any
        if (this.isConnected || this.room) {
            await this.endSession(true);
        }
        this.sessionEndEventEmitted = false;

        // Clean up any existing subscription
        if (this.currentCallSubscription) {
            this.currentCallSubscription.unsubscribe();
            this.currentCallSubscription = null;
        }

        this.isConnecting = true;

        try {
            // Type guard to check if we have token data
            const isTokenData = (
                input: AgentProfile | { wsUrl: string; token: string },
            ): input is {
                wsUrl: string;
                token: string;
                chatMode?: string;
                participantDisplayName?: string;
            } => {
                return 'token' in input && 'wsUrl' in input && typeof input.token === 'string';
            };
            
            if (isTokenData(profileOrToken)) {
                // Direct token provided - use it immediately
                await this.setupCallSession(
                    profileOrToken.token,
                    profileOrToken.chatMode,
                    profileOrToken.wsUrl,
                    profileOrToken.participantDisplayName,
                );
            } else {
                // Agent profile provided - fetch token from API
                if (!profileOrToken.uuid) {
                    throw new Error("Agent profile must have a UUID to initiate web call");
                }

                this.currentCallSubscription = this.intiateWebcall(profileOrToken.uuid).subscribe({
                    next: async (response: any) => {
                        try {
                            // Extract token from response (adjust based on actual API response structure)
                            const token = response?.token || response?.data?.token;
                            
                            if (!token) {
                                throw new Error("Token not found in API response");
                            }
                            
                            await this.setupCallSession(token, response?.chatMode, response?.wsUrl);
                        } catch (error) {
                            console.error("Error setting up call session", error);
                            this.handleConnectionError();
                        }
                    },
                    error: (error) => {
                        console.error("Error initiating web call", error);
                        this.handleConnectionError();
                    }
                });
            }
        } catch (error) {
            console.error("Error in startWebCall", error);
            this.handleConnectionError();
        }
    }

    /**
     * Centralized error handling for connection failures
     */
    private handleConnectionError(): void {
        this.isConnecting = false;
        this.isConnected = false;
        this.endSession(true);
    }

    private async setupCallSession(
        token: string,
        chatMode?: string,
        wsUrl?: string,
        participantDisplayName?: string,
    ) {
        try {
            // Room options
            this.room = new Room();

            const wsURL = wsUrl || environment.livekitWebSocketUrl;
            
            // Register handlers BEFORE connecting
            this.handleRoomEvents();
            this.handleAudioEvents();
            this.handleDataEvents();
            this.handleRpcEvents();

            // Connect to room
            await this.room.connect(wsURL, token);

            const display = participantDisplayName?.trim();
            if (display) {
                try {
                    await this.room.localParticipant.setName(display);
                } catch {
                    /* non-fatal: transcript may fall back to “Visitor” on worker */
                }
            }

            // Text-only (ChatWidgetText): skip audio track per https://docs.livekit.io/agents/multimodality/text/#text-only-sessions
            const isTextOnly = chatMode === chatWidgetType.ChatWidgetText;
            if (!isTextOnly) {
                this.audioTrack = await createLocalAudioTrack({
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    deviceId: undefined,
                });
                await this.room.localParticipant.publishTrack(this.audioTrack);
            } else {
                // Text-only: no agent audio track; emit connected so UI exits loading state
                this.onSocketConnected.next('connected');
            }

            this.isConnected = true;
            this.isConnecting = false;                    
        } catch (err) {
            console.error("Error starting call", err);
            this.isConnecting = false;
            this.isConnected = false;
            this.endSession(true);
        }

    }

    /**
     * Mute or unmute the local audio track.
     * @param muted true to mute, false to unmute
     */
    public async setMuted(muted: boolean) {
        this.isMuted = muted;
        if (this.audioTrack) {
            if (muted) {
                await this.audioTrack.mute();
            } else {
                await this.audioTrack.unmute();
            }
        }
    }



    private handleRoomEvents(): void {
        if (!this.room) return;

         this.room.on(RoomEvent.ConnectionStateChanged, (state) => {
          if (state === ConnectionState.Disconnected) {
            this.cleanup();
          }
        });

        this.room.on(RoomEvent.ParticipantConnected, (participant: RemoteParticipant) => {
            const role = this.getParticipantRole(participant.identity);

            if (role === 'agent') {
                participant.on(ParticipantEvent.TrackSubscribed, (track, publication) => {
                    if (track.kind === Track.Kind.Audio) {
                        console.log("participant track", participant);
                        this.handleAgentAudioTrack(track as RemoteAudioTrack);
                    }
                });
            }
        });

        this.room.on(
            RoomEvent.ParticipantDisconnected,
            (participant: RemoteParticipant) => {
                const identity = (participant?.identity ?? '').trim();
                if (!this.isRoomStaffIdentity(identity)) return;
                const displayName = this.staffDisplayNameByIdentity.get(identity);
                const remaining = this.countRemoteStaffParticipants();
                const lastHuman = remaining === 0;
                
                this.onHumanAgentPresent.next({
                    humanLeft: true,
                    leftIdentity: identity || undefined,
                    ...(displayName ? { agentName: displayName } : {}),
                    lastHuman,
                });
                this.performRpcGeneric({
                    method: TAKEOVER_RPC_METHOD,
                    payload: { action: 'takeover_end', participantIdentity: identity },
                    destinationIdentity: 'agent',
                }).catch(() => {});
                if (lastHuman) {
                    this.clearStaffMaps();
                } else {
                    this.removeStaffImageForIdentity(identity);
                    this.staffDisplayNameByIdentity.delete(identity);
                }
            },
        );

        this.room.on(RoomEvent.Disconnected, () => {
            // room disconnected
            console.log("Room disconnected");
            this.isConnected = false;
            this.endSession(true);
        });

        // Add connection state tracking
        this.room.on(RoomEvent.Connected, () => {
            console.log("Room connected");
            this.isConnected = true;
            this.isConnecting = false;
        });
    }

    private handleAudioEvents(): void {
        if (!this.room) return;

        this.room.on(
            RoomEvent.TrackSubscribed,
            (
                track: RemoteTrack,
                publication: RemoteTrackPublication,
                participant: RemoteParticipant,
            ) => {
                const role = this.getParticipantRole(participant.identity);
                if (track.kind === Track.Kind.Audio && role === 'agent') {
                    this.handleAgentAudioTrack(track as RemoteAudioTrack);

                   
                }
            },
        );
    }


    handleAgentAudioTrack(track: RemoteAudioTrack) {
        if (this.agentAudioElement) {
          this.agentAudioElement.remove();
        }
        
        this.agentAudioElement = document.createElement("audio");
        this.agentAudioElement.autoplay = true;
        this.agentAudioElement.volume = 1.0;
        this.agentAudioElement.srcObject = new MediaStream([track.mediaStreamTrack]);
        
        document.body.appendChild(this.agentAudioElement);
        
        this.agentAudioElement.play().catch(e => {
          document.addEventListener('click', () => {
            this.agentAudioElement.play();
          }, { once: true });
        });

        this.onSocketConnected.next("connected");
        
    }


    private handleRpcEvents(): void { 


        this.room.registerRpcMethod(
            TAKEOVER_RPC_METHOD,
            async (payload: string | object) => {
                try {
                    const { action, humanAgentName, watchingOnly, humanAgentIdentity, humanAgentImage } =
                        parseTakeoverRpcPayload(payload);
                    if (action === 'human_arrived') {
                        this.registerStaffProfile(humanAgentIdentity, humanAgentName, humanAgentImage);
                        this.onHumanAgentPresent.next({
                            additionalJoin: true,
                            ...(humanAgentName ? { agentName: humanAgentName } : {}),
                            ...(humanAgentIdentity ? { agentIdentity: humanAgentIdentity } : {}),
                            ...(humanAgentImage ? { agentImage: humanAgentImage } : {}),
                            watchingOnly,
                        });
                    } else if (action === 'takeover_start' || action === 'pause') {
                        this.registerStaffProfile(humanAgentIdentity, humanAgentName, humanAgentImage);
                        this.onHumanAgentPresent.next({
                            present: true,
                            ...(humanAgentName ? { agentName: humanAgentName } : {}),
                            ...(humanAgentIdentity ? { agentIdentity: humanAgentIdentity } : {}),
                            ...(humanAgentImage ? { agentImage: humanAgentImage } : {}),
                        });
                    } else if (action === 'takeover_end' || action === 'resume') {
                        this.clearStaffMaps();
                        this.onHumanAgentPresent.next({ present: false });
                    }
                } catch (e) {
                    console.warn(TAKEOVER_RPC_METHOD, 'payload parse failed', e);
                }
                return JSON.stringify({ status: 'ok' });
            }
        );
    }
    private handleDataEvents(): void {
        if (!this.room) return;

        this.room.on(
            RoomEvent.Connected,
            () => {
             
            }
        );

        // human_chat: messages sent by participants (end user or human agent) on this topic.
        // Route by participant identity: human / numeric → human agent, enduser-* → end user.
        // Do NOT emit human messages here: worker echoes them with sender name on chat_response_human.
        // Emitting here would duplicate the message in the widget (once without name, once with name).
        this.room.registerTextStreamHandler('human_chat', async (reader, participantInfo) => {
            try {
                const message = await reader.readAll();
                const identity = participantInfo?.identity ?? '';
                const role = this.getParticipantRole(identity);
                if (role === 'enduser') {
                    this.onTranscription.next(message);
                }
                // human role: worker sends same message on chat_response_human with name; only that is shown in UI
            } catch (err) {
                console.error('Error reading human_chat stream', err);
            }
        });

        // chat_response_human: worker sends { name, message } JSON so widget/dashboard can show agent name.
        // Worker rebroadcasts visitor lk.chat so spectators see typed messages (human_chat is not used for AI mode).
        this.room.registerTextStreamHandler('visitor_lk_chat', async (reader) => {
            try {
                const message = await reader.readAll();
                const t = message?.trim();
                if (!t) return;
                this.onEndUserChatText.next(t);
                if (isGcsAudioUrl(t) || isGcsImageUrlInText(t)) {
                    this.skipNextUserTranscription = true;
                }
            } catch (err) {
                console.error('Error reading visitor_lk_chat stream', err);
            }
        });

        // Legacy one-shot AI / system lines on topic chat_response (e.g. max duration warning from worker).
        this.room.registerTextStreamHandler('chat_response', async (reader) => {
            try {
                const message = (await reader.readAll())?.trim();
                if (message) this.onTextResponse.next(message);
            } catch (err) {
                console.error('Error reading chat_response stream', err);
            }
        });

        this.room.registerTextStreamHandler('chat_response_human', async (reader) => {
            try {
                const raw = await reader.readAll();
                const trimmed = raw?.trim();
                if (!trimmed) return;
                try {
                    const parsed = JSON.parse(trimmed) as { name?: string; message?: string; image?: string };
                    if (typeof parsed?.message === 'string') {
                        const name = parsed.name?.trim();
                        const image =
                            (typeof parsed.image === 'string' && parsed.image.trim()) ||
                            (name ? this.staffImageByName.get(name) : undefined);
                        this.onHumanAgentTextResponse.next({
                            name,
                            message: parsed.message,
                            ...(image ? { image } : {}),
                        });
                        return;
                    }
                } catch {
                    // not JSON, treat as plain message
                }
                this.onHumanAgentTextResponse.next({ message: trimmed });
            } catch (err) {
                console.error('Error reading chat_response_human stream', err);
            }
        });

        // lk.transcription: TextStreamReader's async iterator yields per-chunk deltas (see livekit-client
        // readAll: for-await then +=). We must concatenate; treating each yield as the full string only
        // showed the last chunk (often a sentence suffix).
        this.room.registerTextStreamHandler('lk.transcription', async (reader, participantInfo) => {
            try {
                const identity = participantInfo?.identity ?? '';
                const isAgent = identity.startsWith('agent-');
                if (isAgent) {
                    let accumulated = '';
                    const textReader = reader as AsyncIterable<string>;
                    for await (const chunk of textReader) {
                        accumulated += chunk;
                        this.onAgentAiTextStream.next({ text: accumulated, done: false });
                    }
                    this.onAgentAiTextStream.next({ text: accumulated, done: true });
                    this.onTextResponse.next(accumulated);
                    return;
                }
                const message = (await reader.readAll())?.trim() ?? '';
                if (!message) return;
                const attrs = (reader as { info?: { attributes?: Record<string, unknown> } }).info?.attributes ?? {};
                const isFinal = attrs['lk.transcription_final'];
                if (isFinal === false || isFinal === 'false' || isFinal === '0') {
                    return;
                }
                if (this.skipNextUserTranscription) {
                    this.skipNextUserTranscription = false;
                    return;
                }
                if (isImageContentTranscriptionEcho(message)) {
                    return;
                }
                this.onTranscription.next(message);
            } catch (err) {
                console.error('Error reading lk.transcription stream', err);
            }
        });

        this.room.on(
            RoomEvent.DataReceived,
            (
                payload: Uint8Array,
                participant?: Participant,
                kind?: DataPacket_Kind,
                topic?: string,
            ) => {
                const decoder = new TextDecoder();
                try {
                    if (this.tryDispatchTypingDataPacket(payload, participant, topic)) {
                        return;
                    }

                    if (participant?.identity !== "server") return;

                    let decodedData = decoder.decode(payload);
                    let event = JSON.parse(decodedData);
                    if (event.event_type === "update") {
                        console.log("update", event);
                    } else if (event.event_type === "metadata") {
                        console.log("metadata", event);
                    }
                    else if (event.event_type === "agent_start_talking") {
                        console.log("agent_start_talking");
                    } else if (event.event_type === "agent_stop_talking") {
                        console.log("agent_stop_talking");
                    } else if (event.event_type === "node_transition") {
                        console.log("node_transition", event);
                    }
                } catch (err) {
                    console.error("Error decoding data received", err);
                }
            },
        );
    }

    public async endSession(sendEndCallEvent: boolean = true) {

        if (this.room) {
            try {
                this.room.disconnect();
            } catch (error) {
                console.error("Error disconnecting room:", error);
            }
            this.room = null;
        }
        
        // Clean up subscription
        if (this.currentCallSubscription) {
            this.currentCallSubscription.unsubscribe();
            this.currentCallSubscription = null;
        }

        if(this.audioTrack) {
            try {
                this.audioTrack.stop();
                this.audioTrack = null;
            } catch (error) {
                console.error("Error stopping audio track:", error);
            }
        }

        if(this.room) {
            this.room.removeAllListeners();
            this.room = null;
        }

        // Reset state
        this.isConnected = false;
        this.isConnecting = false;
        this.skipNextUserTranscription = false;
        this.clearStaffMaps();

        if (sendEndCallEvent && !this.sessionEndEventEmitted) {
            this.sessionEndEventEmitted = true;
            this.onEndCall.next(true);
        }

        if(this.agentAudioElement) {
            this.agentAudioElement.remove();
            this.agentAudioElement = null;
        }

        // Play end call sound
        try {
            // let sound = new Audio('assets/end-call.mp3');
            // sound.play();
        } catch (error) {
            console.error("Error playing end call sound:", error);
        }
    }

    async GetDevice(id: string) {
        try {
            let devices = await navigator.mediaDevices.enumerateDevices();
            
            // find audio input device and return it
            for (let i = 0; i < devices.length; i++) {
                if (devices[i].kind == "audioinput" && devices[i].deviceId == id) {
                    return devices[i];
                }
            }
            
            return null;
        } catch (error) {
            console.error("Error getting device:", error);
            return null;
        }
    }

    // Add cleanup method for component destruction
    public cleanup() {
        this.endSession(true);
    }
}